import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Send, MessagesSquare } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "pending", "resolved", "closed"];
const PRIO = ["low", "normal", "high", "urgent"];
const emptyNew = { subject: "", body: "", priority: "normal" };

export default function Support({ portal = false }) {
  const { t } = useLang();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyNew);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data);
      setSelected((cur) => (cur ? data.find((x) => x.ticket_id === cur.ticket_id) || null : cur));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post("/support/tickets", form); toast.success(t("common.create")); setOpen(false); setForm(emptyNew); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const send = async () => {
    if (!msg.trim() || !selected) return;
    try { await api.post(`/support/tickets/${selected.ticket_id}/messages`, { body: msg }); setMsg(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setStatus = async (s) => {
    try { await api.patch(`/support/tickets/${selected.ticket_id}`, { status: s }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title={t("tickets.title")} subtitle={t("tickets.subtitle")}
      right={<button data-testid="ticket-new" onClick={() => setOpen(true)} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> {t("tickets.new")}</button>}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tickets-layout">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 divide-y divide-zinc-800 lg:col-span-1 max-h-[calc(100vh-220px)] overflow-auto">
          {tickets.map((tk) => (
            <button key={tk.ticket_id} onClick={() => setSelected(tk)} data-testid={`ticket-item-${tk.ticket_id}`} className={`w-full text-left p-3 hover:bg-zinc-900 ${selected?.ticket_id === tk.ticket_id ? "bg-zinc-900" : ""}`}>
              <div className="text-sm text-zinc-100 truncate">{tk.subject}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-widest ${tk.status === "resolved" ? "text-emerald-400" : tk.status === "closed" ? "text-zinc-500" : "text-blue-400"}`}>{tk.status}</span>
                <span className="text-[10px] text-zinc-500">· {tk.priority}</span>
              </div>
              <div className="text-[11px] text-zinc-500 truncate mt-1">{tk.body}</div>
            </button>
          ))}
          {tickets.length === 0 && <div className="p-6 text-center text-xs text-zinc-500">{t("common.no_data")}</div>}
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 lg:col-span-2 flex flex-col min-h-[400px] max-h-[calc(100vh-220px)]">
          {selected ? (
            <>
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-sm text-zinc-100 font-medium">{selected.subject}</div>
                  <div className="text-[11px] text-zinc-500">{selected.priority} · {selected.status}</div>
                </div>
                <select value={selected.status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-status" className="h-8 bg-zinc-950 border border-zinc-800 rounded px-2 text-xs">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="ticket-messages">
                {(selected.messages || []).map((m, i) => (
                  <div key={`${m.at || i}-${m.body?.slice(0, 20) || i}`} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                    <div className="text-xs text-zinc-500">{m.at?.slice(0, 16).replace("T", " ")}</div>
                    <div className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-zinc-800 p-3 flex gap-2">
                <input data-testid="ticket-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a reply…" className="flex-1 h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
                <button data-testid="ticket-message-send" onClick={send} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white text-sm hover:bg-blue-400 flex items-center gap-1"><Send className="w-3.5 h-3.5" /> {t("common.send")}</button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-zinc-500 text-sm">
              <div className="text-center"><MessagesSquare className="w-8 h-8 mx-auto mb-2 text-blue-400" /> Select a ticket</div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle>{t("tickets.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="ticket-field-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
            <textarea data-testid="ticket-field-body" rows={4} placeholder="Describe your issue…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            <select data-testid="ticket-field-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
              {PRIO.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="ticket-save" onClick={create} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
